import { type SxProps, TextField, type Theme } from '@mui/material';
import React from 'react';

interface NumberInputProps {
    max?: number;
    min?: number;
    value: number;
    type?: 'float' | 'int';
    disabled?: boolean;
    helperText?: string;
    sx?: SxProps<Theme>;
    onChange: (value: number) => void;
}

/**
 * Modern NumberInput component with MUI styling.
 * @param {NumberInputProps} props - The props for the number input.
 * @returns {JSX.Element} A JSX element representing the number input.
 */
export default function NumberInput({ disabled, min, max, value, type, helperText, sx, onChange }: NumberInputProps) {
    const innerType = type || 'int';

    return (
        <TextField
            variant="outlined"
            size="small"
            disabled={disabled}
            type="number"
            value={value}
            helperText={helperText}
            onChange={(event) => {
                const valueIn = event.target.value;
                let valueOut = 0;
                try {
                    if (innerType === 'int') {
                        try {
                            valueOut = parseInt(valueIn);
                        } catch (error) {
                            valueOut = Math.floor(parseFloat(valueIn));
                        }
                    } else if (innerType === 'float') {
                        valueOut = Number.parseFloat(valueIn);
                    }
                    if (max !== undefined && valueOut > max) {
                        valueOut = max;
                    } else if (min !== undefined && valueOut < min) {
                        valueOut = min;
                    }
                    onChange(valueOut);
                } catch (error) {
                    // Handle error silently
                }
            }}
            InputProps={{
                inputProps: {
                    max: max,
                    min: min,
                },
            }}
            sx={{
                width: 'auto',
                minWidth: '120px',
                '& .MuiInputBase-input': {
                    fontSize: '0.875rem',
                },
                '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                },
                ...sx,
            }}
        />
    );
}
